import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  IAuthResponse,
  IPublicBusinessConfig,
} from '@g99-product-web-ui/shared-interface-lib';
import {
  ANALYTICS_EVENTS,
  API_ENDPOINTS,
  LOCAL_STORAGE_KEYS,
} from '@g99-product-web-ui/shared-util-lib';
import { BehaviorSubject, catchError, finalize, of } from 'rxjs';

import { LocalStorageService } from './localStorage.service';
import { ServiceType } from '../../constants/service-types.enum';
import { HttpService } from '../../core/http-service/http.service';
import { AnalyticsProxyService } from '../analytics-service/analytics-proxy.service';
import { SessionTrackingService } from '../analytics-service/session-tracking.service';
import { ToastService } from '../toast-service/toast.service';

@Injectable({
  providedIn: 'root',
})
export class SubjectManageService {
  public userSubject = new BehaviorSubject<IAuthResponse | null>(null);
  public businessSubject = new BehaviorSubject<IPublicBusinessConfig | null>(
    null
  );

  public userSubject$ = this.userSubject.asObservable();

  private _autoLogoutTimer: ReturnType<typeof setTimeout> | null = null;
  private _ownIdToken: string | null = null;

  constructor(
    private readonly _localStorageService: LocalStorageService,
    private readonly _router: Router,
    private readonly _analyticsService: AnalyticsProxyService,
    private readonly _sessionTrackingService: SessionTrackingService,
    private readonly _toastService: ToastService,
    private readonly _httpService: HttpService
  ) {
    this.userSubject.subscribe((user) => {
      if (user?.idToken) {
        this._ownIdToken = user.idToken;
      }
    });

    this._listenForCrossTabSessionConflicts();
  }

  public initializeSubjects(): void {
    const currentUser = this._localStorageService.readItem<IAuthResponse>(
      LOCAL_STORAGE_KEYS.CURRENT_USER
    );
    const businessInfo =
      this._localStorageService.readItem<IPublicBusinessConfig>(
        LOCAL_STORAGE_KEYS.BUSINESS_INFO
      );

    if (currentUser) {
      this.userSubject.next(currentUser);

      if (
        !this._localStorageService.readItem(
          LOCAL_STORAGE_KEYS.SESSION_START_TIME
        )
      ) {
        this._localStorageService.storeItem(
          LOCAL_STORAGE_KEYS.SESSION_START_TIME,
          Date.now()
        );
      }

      try {
        this._analyticsService.initHeartbeat();
        this._analyticsService.initPageTracking();
        this._sessionTrackingService.start();
      } catch (e) {
        console.error('Analytics initialization failed', e);
      }
    }

    if (businessInfo) {
      this.businessSubject.next(businessInfo);
    }
  }

  public logout(preserveReturnUrl = false, logoutReason = 'manual'): void {
    this._clearAutoLogoutTimer();
    this._ownIdToken = null;

    let returnUrl: string | null = null;
    if (preserveReturnUrl) {
      returnUrl = this._localStorageService.readItem(
        LOCAL_STORAGE_KEYS.RETURN_URL
      ) as string;
    }

    try {
      this._analyticsService.track(ANALYTICS_EVENTS.AUTH.USER_LOGOUT_SUCCESS, {
        ...this.getSessionProperties(),
        logout_reason: logoutReason,
      });

      this._analyticsService.stopHeartbeat();
      this._analyticsService.stopPageTracking();
      this._sessionTrackingService.stop(logoutReason);
    } catch (e) {
      console.error('Logout analytics failed', e);
    }

    const currentUser = this._localStorageService.readItem<IAuthResponse>(
      LOCAL_STORAGE_KEYS.CURRENT_USER
    );

    const performClientCleanup = (): void => {
      this._localStorageService.clearStorage();

      this.userSubject.next(null);
      this.businessSubject.next(null);

      if (returnUrl) {
        this._localStorageService.storeItem(
          LOCAL_STORAGE_KEYS.RETURN_URL,
          returnUrl
        );
      }

      this._router.navigate(['/']);
    };

    // A support (impersonation) session runs on the admin portal user's own Cognito tokens - the
    // admin app hands them over via /auth/support. Revoking them on the server here would also
    // kill the still-open admin portal session (its next call, e.g. minting another support link,
    // gets "Token has been revoked" and the admin is force-logged-out). So for support sessions
    // only clear this app's state and leave the tokens to the admin portal that owns them.
    const isSupportSession =
      currentUser?.supportUser === true || currentUser?.supportUser === 'true';

    if (
      !isSupportSession &&
      (currentUser?.refreshToken ||
        currentUser?.accessToken ||
        currentUser?.idToken)
    ) {
      const payload = {
        idToken: currentUser?.idToken || '',
        refreshToken: currentUser?.refreshToken || '',
        accessToken: currentUser?.accessToken || '',
      };
      this._httpService
        .post<
          unknown,
          { idToken: string; refreshToken: string; accessToken: string }
        >(
          API_ENDPOINTS.AUTH_ENDPOINTS.logout,
          payload,
          {
            attachTenantId: false,
          },
          ServiceType.AUTH
        )
        .pipe(
          catchError(() => of(null)),
          finalize(() => performClientCleanup())
        )
        .subscribe();
    } else {
      performClientCleanup();
    }
  }

  public autoLogout(): void {
    this._clearAutoLogoutTimer();
    this._ownIdToken = null;

    const currentUrl = this._router.url;

    if (this.isValidReturnUrl(currentUrl)) {
      this._localStorageService.storeItem(
        LOCAL_STORAGE_KEYS.RETURN_URL,
        currentUrl
      );
    }

    try {
      this._analyticsService.track(ANALYTICS_EVENTS.AUTH.USER_LOGOUT_SUCCESS, {
        reason: 'Session Expired',
        ...this.getSessionProperties(),
      });

      this._analyticsService.stopHeartbeat();
      this._analyticsService.stopPageTracking();
    } catch (e) {
      console.error('Auto-logout analytics failed', e);
    }

    this._localStorageService.clearStorage();
    this.userSubject.next(null);

    this.businessSubject.next(null);

    if (this.isValidReturnUrl(currentUrl)) {
      this._localStorageService.storeItem(
        LOCAL_STORAGE_KEYS.RETURN_URL,
        currentUrl
      );
    }

    this._router.navigate(['/']);
  }

  public startAutoLogoutTimer(expirationTime: number): void {
    this._clearAutoLogoutTimer();

    const timeLeft = expirationTime - Date.now();

    if (timeLeft > 0) {
      this._autoLogoutTimer = setTimeout(() => {
        this.autoLogout();
      }, timeLeft);
    } else {
      this.autoLogout();
    }
  }

  public isValidReturnUrl(url: string): boolean {
    const trimmedUrl = url?.trim();

    if (!trimmedUrl || trimmedUrl === '/') {
      return false;
    }

    const excludedRoutes = [
      '/login',
      '/sign-in',
      '/business-selection',
      '/forgot-password',
      '/reset-password',
      '/verify-email',
      '/change-password',
      '/mfa-setup',
      '/mfa-verify',
      '/support',
      '/redirectFromOldUI',
    ];

    const normalizedUrl =
      trimmedUrl.endsWith('/') && trimmedUrl.length > 1
        ? trimmedUrl.slice(0, -1)
        : trimmedUrl;

    return !excludedRoutes.some(
      (route) =>
        normalizedUrl === route || normalizedUrl.startsWith(route + '/')
    );
  }

  public getSessionProperties(): Record<string, any> {
    const startTime = this._localStorageService.readItem<number>(
      LOCAL_STORAGE_KEYS.SESSION_START_TIME
    );
    if (!startTime) {
      return {};
    }

    const durationMs = Date.now() - startTime;
    const durationSeconds = Math.floor(durationMs / 1000);

    return {
      session_duration_seconds: durationSeconds,
      session_duration_minutes: Number((durationSeconds / 60).toFixed(2)),
      platform: 'web',
    };
  }

  private _clearAutoLogoutTimer(): void {
    if (this._autoLogoutTimer) {
      clearTimeout(this._autoLogoutTimer);
      this._autoLogoutTimer = null;
    }
  }

  private _listenForCrossTabSessionConflicts(): void {
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.storageArea !== localStorage) {
        return;
      }

      if (event.key === null) {
        if (this.userSubject.value) {
          this._invalidateSessionDueToConflict();
        }
        return;
      }

      if (event.key !== LOCAL_STORAGE_KEYS.CURRENT_USER) {
        return;
      }

      const previousUser = this.userSubject.value;
      if (!previousUser) {
        return;
      }

      if (!event.newValue) {
        return;
      }

      let newUser: IAuthResponse | null = null;
      try {
        newUser = JSON.parse(event.newValue);
      } catch {
        return;
      }

      const sameSession =
        newUser?.idToken && newUser.idToken === this._ownIdToken;
      if (sameSession) {
        return;
      }

      this._invalidateSessionDueToConflict();
    });
  }

  private _invalidateSessionDueToConflict(): void {
    this._clearAutoLogoutTimer();
    this._ownIdToken = null;

    try {
      this._analyticsService.stopHeartbeat();
      this._analyticsService.stopPageTracking();
    } catch (e) {
      console.error('Cross-tab session invalidation analytics failed', e);
    }

    this.userSubject.next(null);
    this.businessSubject.next(null);
    this._toastService.showError(
      'Your session has expired because this account was logged in from another window.'
    );
    this._router.navigate(['/']);
  }
}
